import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LineChartComponent } from "../../../../ChartComponents/LineChartFluent";
import CustomCard from "../../../../SharedComponents/CustomCard";
import { selectViewport } from "../../../../slices/UI.slice";
import { useAppSelector } from "../../../../store/hooks";

interface TrendPoint {
  snapshotDate: Date;
}

interface TrendSeries<T> {
  color: string;
  getValue: (point: T) => number;
  legend: string;
}

interface MultiSeriesTrendChartProps<T> {
  cardTitle: string;
  data: T[];
  isLoading: boolean;
  kustoLink?: string;
  series: TrendSeries<T>[];
}

const MultiSeriesTrendChart = <T extends TrendPoint,>({
  cardTitle,
  data,
  isLoading,
  kustoLink,
  series
}: MultiSeriesTrendChartProps<T>) => {
  const widthContainer = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(500);
  const resizedWidth = useAppSelector((state) => selectViewport(state).offsetWidth);
  const handleResize = useCallback(setWidth, []);

  useEffect(() => {
    if (widthContainer.current) {
      handleResize(widthContainer.current.offsetWidth);
    }
  }, [handleResize, resizedWidth]);

  const chartData = useMemo(
    () => ({
      chartTitle: cardTitle,
      lineChartData: series.map((item) => ({
        color: item.color,
        legend: item.legend,
        data: data.map((point) => {
          const date = new Date(point.snapshotDate);
          return {
            x: date,
            y: item.getValue(point),
            xAxisCalloutData: date.toLocaleDateString("en-US", { month: "short", year: "numeric" })
          };
        })
      }))
    }),
    [cardTitle, data, series]
  );

  return (
    <div ref={widthContainer} style={{ minWidth: 0, width: "100%" }}>
      <CustomCard cardHeaderTitle={cardTitle} chartTitle={cardTitle} kustoLink={kustoLink} isLoading={isLoading}>
        <LineChartComponent
          allowResize
          data={chartData}
          height={400}
          isLoading={isLoading}
          width={Math.max(width - 10, 0)}
        />
      </CustomCard>
    </div>
  );
};

export default MultiSeriesTrendChart;
